import { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import * as AlertNotification from '@givebutter/primitives/alert-notification';
import { BoxIcon } from '@givebutter/primitives/box-icon';
import { Button } from '@givebutter/primitives/button';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { useToast } from '@givebutter/primitives/toast';
import { faArrowDown, faArrowUp, faSave } from '@fortawesome/pro-regular-svg-icons';
import { faEnvelope } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    createOrUpdatePersonalizedLetter,
    fetchEngageMailPiecePreviewStatus,
    removeContactFromRecipientsList,
    revertPersonalizedLetter,
    undoRemoveContactFromRecipientsList,
} from 'api/engage';
import { PersonalizedLetterStatusBadge } from 'assets/js/pages/engage/letters/PersonalizedLetterStatusBadge';
import { useCurrentLetter } from 'assets/js/pages/engage/letters/useCurrentLetter';
import { usePersonalizedLetterContactsQuery } from 'assets/js/pages/engage/letters/usePersonalizedLetterContactsQuery';
import { usePersonalizedLettersStore } from 'assets/js/pages/engage/letters/usePersonalizedLettersStore';
import { useSetPersonalizedContact } from 'assets/js/pages/engage/letters/useSetPersonalizedContact';
import * as DropdownMenu from 'components/dropdown-menu';
import { useAppState } from 'customHooks/useAppState';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact, LetterFulfillment } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

const PersonalizedLetterCurrentContactHeader = () => {
    const { accountId } = useCurrentAccount();
    const { data: letter } = useCurrentLetter();
    const { success, error } = useToast();
    const { apiUrl } = useAppState();
    const queryClient = useQueryClient();
    const {
        hasDiffFromMasterLetter,
        setHasDiffFromMasterLetter,
        currentPersonalizedLetterContact,
        setCurrentPersonalizedLetterContact,
        editorInstance,
    } = usePersonalizedLettersStore();
    const setPersonalizedContact = useSetPersonalizedContact();

    const { data, isPending } = usePersonalizedLetterContactsQuery();

    const contacts = useMemo(() => data.pages ?? [], [data.pages]);

    const selectedContactIndex = useMemo(
        () =>
            contacts.findIndex(contact => contact.id === currentPersonalizedLetterContact?.id) ?? 0,
        [currentPersonalizedLetterContact?.id, contacts],
    );

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: accountQueryKeys.contacts(accountId) });
        queryClient.invalidateQueries({
            queryKey: accountQueryKeys.engageMessageById(accountId, letter.id),
        });
    };

    const downloadPreview = useCallback(() => {
        const downloadUrl = `${apiUrl}/dashboard/accounts/${accountId}/messages/${letter.id}/contacts/${currentPersonalizedLetterContact?.id}/download-preview`;
        window.open(downloadUrl, '_blank');
    }, [accountId, apiUrl, currentPersonalizedLetterContact?.id, letter.id]);

    const { mutate: fetchMailPiecePreview, isPending: isPreviewPending } = useMutation({
        mutationFn: async () => {
            const fetchPreview = () =>
                fetchEngageMailPiecePreviewStatus(
                    accountId,
                    letter.id,
                    currentPersonalizedLetterContact?.id ?? 0,
                );
            let timeout: NodeJS.Timeout;
            const fetchPreviewRecursive = async () => {
                const previewRes = await fetchPreview();
                if (previewRes.status) {
                    clearTimeout(timeout);
                    return previewRes;
                } else {
                    return new Promise(resolve => {
                        timeout = setTimeout(async () => {
                            resolve(await fetchPreviewRecursive());
                        }, 5000);
                    });
                }
            };
            return fetchPreviewRecursive();
        },
        onSuccess: () => {
            downloadPreview();
            setCurrentPersonalizedLetterContact({
                ...(currentPersonalizedLetterContact as Contact),
                mail_piece: {
                    ...currentPersonalizedLetterContact?.mail_piece,
                    is_personalized_preview_ready: true,
                } as LetterFulfillment,
            });
        },
        onError: (errorRes: GivebutterAxiosError) => {
            error(errorRes.response?.data?.message ?? 'Error generating letter preview.');
        },
    });

    const { mutate: updatePersonalizedLetter } = useMutation({
        mutationFn: () => {
            const body = editorInstance?.getContent();

            if (body && currentPersonalizedLetterContact) {
                return createOrUpdatePersonalizedLetter(
                    accountId,
                    letter.id,
                    currentPersonalizedLetterContact.id,
                    {
                        body,
                    },
                );
            }

            return Promise.resolve({} as LetterFulfillment);
        },
        onSuccess: mail_piece => {
            success('Saved personalized letter!');
            setCurrentPersonalizedLetterContact({
                ...(currentPersonalizedLetterContact as Contact),
                mail_piece,
            });
            setHasDiffFromMasterLetter(false);
            invalidate();
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'Failed to save personalized letter'),
    });

    const { mutate: revertToOriginal } = useMutation({
        mutationFn: () => {
            if (currentPersonalizedLetterContact && !isNotPersonalized) {
                return revertPersonalizedLetter(
                    accountId,
                    letter.id,
                    currentPersonalizedLetterContact.id,
                );
            }

            editorInstance?.resetContent();

            return Promise.resolve();
        },
        onSuccess: () => {
            success('Reverted personalized letter!');
            setCurrentPersonalizedLetterContact({
                ...(currentPersonalizedLetterContact as Contact),
                mail_piece: null,
            });
            editorInstance?.setContent(letter.body ?? '');
            editorInstance?.setDirty(false);
            setHasDiffFromMasterLetter(false);
            invalidate();
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'Failed to revert personalized letter'),
    });

    const { mutate: removeContactFromRecipients } = useMutation({
        mutationFn: () => {
            if (currentPersonalizedLetterContact) {
                return removeContactFromRecipientsList(
                    accountId,
                    letter.id,
                    currentPersonalizedLetterContact.id,
                );
            }

            return Promise.resolve({} as LetterFulfillment);
        },
        onSuccess: mail_piece => {
            success('Excluded contact from recipient list!');
            setCurrentPersonalizedLetterContact({
                ...(currentPersonalizedLetterContact as Contact),
                mail_piece,
            });
            invalidate();
            editorInstance?.mode.set('readonly');
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'Failed to exclude contact from recipient list'),
    });

    const { mutate: undoRemoveContactFromRecipients } = useMutation({
        mutationFn: () => {
            if (currentPersonalizedLetterContact) {
                return undoRemoveContactFromRecipientsList(
                    accountId,
                    letter.id,
                    currentPersonalizedLetterContact.id,
                );
            }

            return Promise.resolve({} as LetterFulfillment);
        },
        onSuccess: mail_piece => {
            success('Included contact to recipient list!');
            setCurrentPersonalizedLetterContact({
                ...(currentPersonalizedLetterContact as Contact),
                mail_piece,
            });
            invalidate();
            editorInstance?.mode.set('design');
        },
        onError: (e: GivebutterAxiosError) =>
            error(e.response?.data?.message ?? 'Failed to include contact to recipient list'),
    });

    const nextContact = useCallback(() => {
        const possibleContact = contacts[selectedContactIndex + 1];
        if (possibleContact) setPersonalizedContact(possibleContact);
    }, [contacts, selectedContactIndex, setPersonalizedContact]);

    const prevContact = useCallback(() => {
        const possibleContact = contacts[selectedContactIndex - 1];
        if (possibleContact) setPersonalizedContact(possibleContact);
    }, [contacts, selectedContactIndex, setPersonalizedContact]);

    const isNotPersonalized = !currentPersonalizedLetterContact?.mail_piece?.is_personalized;
    const isRemoved = !!currentPersonalizedLetterContact?.mail_piece?.excluded_at;
    const isSaveDisabled = isRemoved || !hasDiffFromMasterLetter;
    const isRevertDisabled = isRemoved || (isNotPersonalized && !hasDiffFromMasterLetter);
    const isNextContactDisabled = !contacts[selectedContactIndex + 1];
    const isPrevContactDisabled = !contacts[selectedContactIndex - 1];
    const isEditHouseholdDisabled = !(
        currentPersonalizedLetterContact?.household_id &&
        (letter.settings.generate_letter_for === 'all' ||
            letter.settings.generate_letter_for === 'households_only')
    );

    if (!currentPersonalizedLetterContact) {
        return null;
    }

    return (
        <LoadingOverlay isLoading={isPending}>
            {isRemoved && (
                <AlertNotification.Root type="error" className="rounded-none border-none">
                    <AlertNotification.Content>
                        <AlertNotification.Title className="flex items-center justify-center gap-3">
                            <AlertNotification.Icon />
                            This recipient has been excluded from your mailing
                        </AlertNotification.Title>
                    </AlertNotification.Content>
                </AlertNotification.Root>
            )}
            <div
                className={
                    'flex flex-wrap justify-between gap-4 border-b border-b-gray-200 bg-white px-8 py-4'
                }
            >
                <div className={'flex items-start gap-4'}>
                    <BoxIcon size="lg">
                        <FontAwesomeIcon icon={faEnvelope} />
                    </BoxIcon>
                    <div className={'flex flex-col'}>
                        <div className={'flex items-center gap-2'}>
                            <p className={'font-medium'}>
                                {currentPersonalizedLetterContact.first_name}{' '}
                                {currentPersonalizedLetterContact.last_name}
                            </p>
                            {!isRemoved && (
                                <PersonalizedLetterStatusBadge
                                    contact={currentPersonalizedLetterContact}
                                />
                            )}
                        </div>
                        <p className={'text-sm text-gray-700'}>
                            {currentPersonalizedLetterContact.primary_address_formatted}
                        </p>
                    </div>
                </div>
                <div className={'flex items-center gap-2'}>
                    <p className={'whitespace-nowrap text-xs text-gray-500'}>
                        {selectedContactIndex + 1} / {contacts.length}
                    </p>
                    <div className={'flex gap-1'}>
                        <Button
                            variant={'outline'}
                            color={'neutral'}
                            size={'sm'}
                            onClick={() => nextContact()}
                            disabled={isNextContactDisabled}
                        >
                            <FontAwesomeIcon icon={faArrowDown} />
                        </Button>
                        <Button
                            variant={'outline'}
                            color={'neutral'}
                            size={'sm'}
                            onClick={() => prevContact()}
                            disabled={isPrevContactDisabled}
                        >
                            <FontAwesomeIcon icon={faArrowUp} />
                        </Button>
                    </div>
                    {isRemoved && (
                        <Button
                            onClick={() => undoRemoveContactFromRecipients()}
                            variant={'outline'}
                            color={'neutral'}
                            size={'sm'}
                        >
                            Include recipient
                        </Button>
                    )}
                    {!isRemoved && (
                        <Button
                            onClick={() => removeContactFromRecipients()}
                            variant={'outline'}
                            color={'neutral'}
                            size={'sm'}
                        >
                            Exclude recipient
                        </Button>
                    )}
                    <Button
                        className={'flex items-center gap-1.5 whitespace-nowrap'}
                        disabled={isSaveDisabled}
                        onClick={() => updatePersonalizedLetter()}
                        variant={'outline'}
                        color={'neutral'}
                        size={'sm'}
                    >
                        <FontAwesomeIcon icon={faSave} />
                        Save changes
                    </Button>
                    <DropdownMenu.Root>
                        <DropdownMenu.EllipsisTrigger />
                        <DropdownMenu.Content sideOffset={6} align={'end'}>
                            <AlertDialog.Root type={'warning'}>
                                <AlertDialog.Trigger type={'reset'} asChild={true}>
                                    <DropdownMenu.Item
                                        disabled={isRevertDisabled}
                                        onSelect={e => e.preventDefault()}
                                    >
                                        Revert to original
                                    </DropdownMenu.Item>
                                </AlertDialog.Trigger>
                                <AlertDialog.Portal>
                                    <AlertDialog.Overlay />
                                    <AlertDialog.Content>
                                        <AlertDialog.Header>
                                            <AlertDialog.Icon />
                                            <AlertDialog.Close />
                                        </AlertDialog.Header>
                                        <AlertDialog.Body>
                                            <AlertDialog.Title>
                                                Revert letter to original?
                                            </AlertDialog.Title>
                                            <AlertDialog.Description>
                                                This action will revert any personalization applied
                                                to this letter and restore the mailing copy to its
                                                original state.
                                            </AlertDialog.Description>
                                        </AlertDialog.Body>
                                        <AlertDialog.Footer>
                                            <AlertDialog.Cancel>Cancel</AlertDialog.Cancel>
                                            <AlertDialog.Action onClick={() => revertToOriginal()}>
                                                Revert
                                            </AlertDialog.Action>
                                        </AlertDialog.Footer>
                                    </AlertDialog.Content>
                                </AlertDialog.Portal>
                            </AlertDialog.Root>
                            <DropdownMenu.LinkItem
                                to={`/accounts/${accountId}/contacts/${currentPersonalizedLetterContact.id}`}
                            >
                                View contact
                            </DropdownMenu.LinkItem>
                            <DropdownMenu.LinkItem
                                to={`/accounts/${accountId}/contacts/${currentPersonalizedLetterContact.id}/profile/details/edit`}
                            >
                                Edit contact
                            </DropdownMenu.LinkItem>

                            <DropdownMenu.LinkItem
                                disabled={isEditHouseholdDisabled}
                                to={`/accounts/${accountId}/engage/letters/draft/${letter.id}/edit/personalize/contacts/${currentPersonalizedLetterContact.id}/household/${currentPersonalizedLetterContact.household_id}/update`}
                            >
                                Edit household
                            </DropdownMenu.LinkItem>

                            <LoadingOverlay isLoading={isPreviewPending}>
                                <DropdownMenu.Item
                                    onSelect={e => {
                                        e.preventDefault();
                                    }}
                                    onClick={() => {
                                        fetchMailPiecePreview();
                                    }}
                                >
                                    Download preview
                                </DropdownMenu.Item>
                            </LoadingOverlay>
                        </DropdownMenu.Content>
                    </DropdownMenu.Root>
                </div>
            </div>
        </LoadingOverlay>
    );
};

export { PersonalizedLetterCurrentContactHeader };
